import { ArrowLeft, Copy, Pencil, Plus, Trash2 } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link } from 'react-router'
import { toast } from 'sonner'
import { PageTitle } from '@/app/layout'
import { KpiTile } from '@/components/redesign/bits'
import { Button } from '@/components/ui/button'
import { Dialog, DialogClose, DialogContent } from '@/components/ui/dialog'
import { NativeSelect } from '@/components/ui/native-select'
import { SearchField } from '@/components/ui/search-field'
import { Skeleton } from '@/components/ui/skeleton'
import { EmptyState, ErrorState } from '@/components/ui/states'
import { errorMessage } from '@/lib/api/errors'
import type { ReplyTemplate } from '@/lib/api/types'
import { formatRelative, numberFmt } from '@/lib/format'
import { ReplyTemplateEditor } from './reply-template-editor'
import { type ReplyTemplateInput, useDeleteReplyTemplate, useReplyTemplates } from './reply-templates'

type Editing = { heading: string; initial: ReplyTemplateInput; id?: string }

/** The team's reply templates: ready-made ones and their own, to use from any ticket's reply box. */
export function ReplyTemplatesPage() {
  const templates = useReplyTemplates()
  const remove = useDeleteReplyTemplate()
  const [search, setSearch] = useState('')
  const [category, setCategory] = useState('all')
  const [editing, setEditing] = useState<Editing | null>(null)
  const [deleting, setDeleting] = useState<ReplyTemplate | null>(null)

  const list = useMemo(() => {
    const q = search.trim().toLowerCase()
    return (templates.data?.data ?? [])
      .filter(
        (t) => category === 'all' || (category === 'any' ? t.category === null : t.category === category),
      )
      .filter((t) => q === '' || `${t.title}\n${t.body}`.toLowerCase().includes(q))
  }, [templates.data, search, category])

  const title = (
    <PageTitle
      title="Reply templates"
      description="Ready-made answers and the team’s own, to use from any ticket’s reply box. Placeholders such as {customer_first_name} are filled in from the ticket, and every reply can be edited before it’s sent."
      actions={
        <>
          <Link to="/tickets" className="btn">
            <ArrowLeft aria-hidden="true" />
            Support inbox
          </Link>
          {templates.data && (
            <button
              type="button"
              className="btn primary"
              onClick={() =>
                setEditing({ heading: 'New template', initial: { title: '', body: '', category: null } })
              }
            >
              <Plus aria-hidden="true" />
              New template
            </button>
          )}
        </>
      }
    />
  )

  if (templates.isPending)
    return (
      <>
        {title}
        <Skeleton className="h-80 rounded-[14px]" />
      </>
    )
  if (templates.isError)
    return (
      <>
        {title}
        <div className="card">
          <ErrorState error={templates.error} onRetry={() => void templates.refetch()} />
        </div>
      </>
    )

  const { meta } = templates.data
  const labels = Object.fromEntries(meta.categories.map((c) => [c.key, c.label]))
  const all = templates.data.data
  const used = all.reduce((n, t) => n + t.uses, 0)

  return (
    <>
      {title}
      <div className="kpis">
        <KpiTile
          label="Templates"
          value={numberFmt.format(all.length)}
          compare={`${numberFmt.format(all.filter((t) => t.ready_made).length)} ready-made`}
        />
        <KpiTile label="Times used" value={numberFmt.format(used)} compare="in replies, all time" />
        <KpiTile
          label="Never used"
          value={numberFmt.format(all.filter((t) => t.uses === 0).length)}
          compare="worth a look or a delete"
        />
      </div>
      <div className="fbar">
        <SearchField
          label="Search templates"
          placeholder="Search titles and text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--muted)' }}>
          Suggested for
          <NativeSelect
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="h-9 w-56 text-[13px]"
          >
            <option value="all">All templates</option>
            <option value="any">Any ticket</option>
            {meta.categories.map((c) => (
              <option key={c.key} value={c.key}>
                {c.label}
              </option>
            ))}
          </NativeSelect>
        </label>
      </div>

      {list.length === 0 ? (
        <div className="card">
          <EmptyState
            title="No templates here"
            description="Nothing matches this search. Add one with New template."
          />
        </div>
      ) : (
        <ul
          className="cards"
          aria-label="Reply templates"
          style={{ listStyle: 'none', margin: 0, padding: 0 }}
        >
          {list.map((t) => (
            <li key={t.id} className="card dc">
              <div>
                <h2 style={{ fontSize: 15, fontWeight: 600 }}>{t.title}</h2>
                <p
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 6,
                    marginTop: 6,
                    fontSize: 12,
                    color: 'var(--subtle)',
                  }}
                >
                  {t.ready_made ? (
                    <span className="chip">Ready-made</span>
                  ) : (
                    <span className="chip lime">By {t.author ?? 'a former team member'}</span>
                  )}
                  <span>{t.category === null ? 'Any ticket' : (labels[t.category] ?? t.category)}</span>
                </p>
              </div>
              <p
                className="line-clamp-4 whitespace-pre-line"
                style={{ fontSize: 13, lineHeight: 1.55, color: 'var(--muted)', flex: 1 }}
              >
                {t.body}
              </p>
              <div className="dc-row" style={{ borderTop: '1px solid var(--line)', paddingTop: 10 }}>
                <span>
                  Used <b>{numberFmt.format(t.uses)}</b> {t.uses === 1 ? 'time' : 'times'}
                </span>
                <span>{t.last_used_at ? `last ${formatRelative(t.last_used_at)}` : 'not yet used'}</span>
              </div>
              <div className="bar">
                <button
                  type="button"
                  className="btn sm ghost"
                  onClick={() =>
                    setEditing({
                      heading: 'Copy as your own',
                      initial: {
                        title: `${t.title} (my version)`.slice(0, 120),
                        body: t.body,
                        category: t.category,
                      },
                    })
                  }
                >
                  <Copy aria-hidden="true" /> Copy<span className="sr-only"> {t.title}</span>
                </button>
                {t.can_edit && (
                  <span className="acts push">
                    <button
                      type="button"
                      className="btn sm ghost"
                      onClick={() =>
                        setEditing({
                          heading: 'Edit template',
                          id: t.id,
                          initial: { title: t.title, body: t.body, category: t.category },
                        })
                      }
                    >
                      <Pencil aria-hidden="true" /> Edit<span className="sr-only"> {t.title}</span>
                    </button>
                    <button type="button" className="btn sm ghost danger" onClick={() => setDeleting(t)}>
                      <Trash2 aria-hidden="true" /> Delete<span className="sr-only"> {t.title}</span>
                    </button>
                  </span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {editing && (
        <ReplyTemplateEditor
          open
          onOpenChange={(open) => !open && setEditing(null)}
          meta={meta}
          heading={editing.heading}
          initial={editing.initial}
          id={editing.id}
        />
      )}
      <Dialog open={deleting !== null} onOpenChange={(open) => !open && setDeleting(null)}>
        <DialogContent
          title="Delete this template?"
          description={
            deleting?.ready_made
              ? `“${deleting.title}” is ready-made. Once deleted it’s gone for the whole team.`
              : `“${deleting?.title ?? ''}” is removed for the whole team.`
          }
          footer={
            <>
              <DialogClose asChild>
                <Button variant="ghost">Keep it</Button>
              </DialogClose>
              <Button
                variant="danger"
                loading={remove.isPending}
                onClick={() =>
                  deleting &&
                  remove.mutate(deleting.id, {
                    onSuccess: () => {
                      toast.success('Template deleted.')
                      setDeleting(null)
                    },
                    onError: (e) => toast.error(errorMessage(e)),
                  })
                }
              >
                <Trash2 /> Delete template
              </Button>
            </>
          }
        />
      </Dialog>
    </>
  )
}
